import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { getHersheySans } from '../hershey-font.js';
import { cleanDisplayTitle, cleanDisplayArtist } from '../title-clean.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { getRenderBufferHeight } from '../scene-manager.js';
import { BLACKBODY_CHUNK } from './ember.js';

// The sign is the light in the room - it lights its own wall. A rig would be a
// second, contradictory light source.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'ravesign',
  family: 'vector',
  danceability: 0.65,
  energyLevel: 0.80,
  bpmMin: 100,
  bpmMax: 175,
  movement: 0.55,
  tonality: 0.70,
  prefers: { gridStrength: [0.50, 1.0] },
  enabled: true
};

// RaveSign: a neon sign spelling the track title, built as an OBJECT rather
// than as lit type.
//
// The whole design turns on one thing: a neon tube that is switched off does
// not disappear. It is still a glass tube bolted to a painted steel plate, and
// you can see it. That is the difference between a sign and a glowing caption,
// and it is why the dark glass layer is drawn at every tier even where the
// brief economises the others away.
//
// THE FOUR LAYERS, outside in, all from ONE geometry with different widths:
//   halo      22px  the light diffused in the air around the tube
//   glass      9px  the tube itself, dark, faintly translucent, always visible
//   discharge  5px  the ionised gas column, in the gas's own colour
//   core       2px  the hot centre, almost white, and what the bloom catches
//
// One centreline geometry, four meshes, four widths. The strip is expanded in
// the vertex shader from a per-vertex offset direction and a signed side, the
// same fat-line idiom handwriting.js uses - three.js's own Line2 would need a
// separate geometry per width, which is exactly what we are avoiding.
//
// THE FONT is a monoline sans (futural.jhf via tools/build-hershey.mjs). A tube
// is bent from one continuous piece of glass, so the letterforms have to BE
// centrelines; an ordinary font gives closed outlines, and a filled outline is
// a lit shape, not a tube.

// --- layout, in "height units": the frame is 2 tall, y up, matching the safe
// --- zone's own convention so the DOM cover's measured rect drops straight in.
const CAMERA_DIST = 6;
// Reference px sizes (the brief's), converted through the real render buffer.
// 7, not 22. At 22 the halo reached most of the way across the plate and the
// panel stopped being darker than the tubes, which is the whole contrast the
// object depends on. A halo is the air glowing right next to the glass.
const HALO_PX = 7, GLASS_PX = 9, DISCHARGE_PX = 5, CORE_PX = 2;
const SHADOW_PX = 4;          // the tube's shadow on the plate
const CLIP_SPACING_PX = 40;
const TRACK_SPACING = 0.16;   // extra advance between letters, in cap heights
const WORD_SPACE = 0.28;
const LINE_GAP = 1.55;        // baseline to baseline, in cap heights
const ARTIST_SCALE = 0.52;
const PLATE_MARGIN = 0.42;    // in cap heights, around the text block
const MAX_LINE_CHARS = 13;    // beyond this the title wraps to two lines
// The wet floor takes the bottom 35% of the frame; the sign lives above it.
// The ground, in height units. Low: the wall is the subject and the wet is
// what it stands in, not the other way round.
const FLOOR_TOP = -0.48;
// The plate's bottom edge above the ground, in height units - so 0.30 is 15%
// of the frame height. Low enough that the sign sits close to the wet ground
// and its reflection stays tight underneath it, high enough that a clear band
// of brick still reads between the two: it is that gap, not the reflection
// itself, that gives the depth.
const PLATE_LIFT = 0.30;
const PLATE_MAX_FRAC = 0.60;  // of the frame's width

// --- the strike -------------------------------------------------------------
// A neon does not switch on. It STRIKES: the discharge hesitates, flickers two
// to four times at uneven intervals, and only then establishes itself - after
// which it still has to propagate from one electrode to the other. Both halves
// are the most recognisable thing a neon does.
const STRIKE_PULSES_MIN = 2, STRIKE_PULSES_MAX = 4;
const STRIKE_PULSE_MS_MIN = 30, STRIKE_PULSE_MS_MAX = 60;
const STRIKE_GAP_MS_MIN = 25, STRIKE_GAP_MS_MAX = 110;
const PROPAGATE_S = 0.08;     // electrode to electrode
const PROPAGATE_SOFT = 0.10;  // how soft the advancing front is, in arc units

// --- the failing letter -----------------------------------------------------
// Exactly one, chosen per track. It is the detail that makes the object
// believable, and more than one reads as a broken render rather than a broken
// sign.
const FAIL_MIN_S = 3, FAIL_MAX_S = 9;
const FAIL_DARK_MS_MIN = 90, FAIL_DARK_MS_MAX = 420;

// --- the transformer --------------------------------------------------------
// Mains hum at 100 Hz, 3% deep. At 60fps this aliases down to a slow shimmer
// rather than reproducing 100 Hz - which is exactly what a camera pointed at a
// real neon records, and it does the job asked of it: the light is never
// perfectly still. bands.high moves the frequency.
const HUM_HZ_MIN = 72, HUM_HZ_MAX = 128;
const HUM_DEPTH = 0.03;

// --- the garland ------------------------------------------------------------
// Filament bulbs, with the thermal inertia strobewall.js models: they heat in
// about 45ms and cool in about 180ms, so a bulb that has just passed does not
// switch off - it fades down through orange, which is the whole reason a
// running garland reads as lamps rather than as a moving mask.
const BULB_HEAT = 22.0;
const BULB_COOL = 5.5;
const CHASE_LIT = 4;          // bulbs alight at once in the running band

// --- the arrow --------------------------------------------------------------
const ARROW_SEGMENTS = 5;     // lit in sequence, one cycle every two bars

// --- rain and headlights ----------------------------------------------------
const RAIN_SPEED_MIN = 1.7, RAIN_SPEED_MAX = 2.9;   // height units per second
const RAIN_LEN_MIN = 0.045, RAIN_LEN_MAX = 0.10;
const HEADLIGHT_MIN_S = 15, HEADLIGHT_MAX_S = 40;
const HEADLIGHT_SWEEP_S = 1.5;
// A very slow lateral travelling, on top of scene-manager's own drift. Its only
// job is parallax: the sign, its clips and the wall sit at different depths and
// nothing reveals that in a still frame.
const CAM_TRAVEL = 0.4, CAM_PERIOD_S = 55;
// LE MUR DOIT DÉPASSER DU CADRE, et de combien se calcule - ça ne se devine
// pas. Trois choses l'élargissent :
//   - il est posé à z = MUR_Z, donc PLUS LOIN que le plan z = 0 pour lequel
//     _halfH a été calculé : à 6.5 au lieu de 6, il faut 8% de plus.
//   - la caméra de ce visualiseur fait un travelling de +-CAM_TRAVEL.
//   - scene-manager ajoute PAR-DESSUS sa propre dérive permanente, en
//     position et en rotation, sur tous les visualiseurs.
// Mesuré avant correction, en comptant les pixels noirs sur une colonne de dix
// pixels au bord du cadre : 100% de noir sur le bord gauche À L'ARRÊT, et
// jusqu'à 16% sur le bord droit quand la caméra part à gauche. Le mur
// s'arrêtait, tout simplement.
const MUR_Z = -0.5, SOL_Z = -0.4;
const DERIVE_POS = 0.22, DERIVE_ROT = 0.012;   // scene-manager.js
const MARGE_MUR = 0.20;

const BAR_PEAK = 0.15;        // bar downbeat: whole title lit, +15%
const DROP_SIZZLE_BEATS = 1;
const PROGRESS_LIT_MIN = 0.60;  // at the start of a track
const PROGRESS_LIT_FULL = 0.50; // progress at which every letter is lit

// Gas colours. Real neon colour comes from the fill, not from a palette, so the
// artwork's hues are PULLED ONTO the nearest real gas rather than used raw -
// saturation forced up and the hue nudged to the closest of the four fills a
// sign shop actually stocks.
const GAS_HUES = [
  0.035,  // neon: red-orange, the classic
  0.58,   // argon + mercury: blue
  0.74,   // argon: lavender
  0.33,   // coated tube: green
  0.14,   // coated tube: yellow
  0.92    // coated tube: pink
];
const GAS_SAT_MIN = 0.90;

// Each tier lists its layers explicitly as {mode, px}. "fused" is one layer
// doing the glass's job AND the gas's: it is the dark tube when off and the
// discharge when lit, which is how criterion 1 - an unlit tube stays visible -
// survives the tiers where the brief drops the separate glass layer.
const TIER = {
  0: { layers: [{ mode: 'halo', px: HALO_PX }, { mode: 'fused', px: CORE_PX * 2.2 }],
       shadow: false, clips: false, cables: false, litWall: false,
       reflect: [], puddles: false, garland: 28, arrow: false, rain: 0, headlights: false },
  1: { layers: [{ mode: 'halo', px: HALO_PX }, { mode: 'fused', px: DISCHARGE_PX },
                { mode: 'core', px: CORE_PX }],
       shadow: true, clips: true, cables: false, litWall: true,
       reflect: ['halo', 'fused'], puddles: false, garland: 48, arrow: false, rain: 0, headlights: false },
  2: { layers: [{ mode: 'halo', px: HALO_PX }, { mode: 'glass', px: GLASS_PX },
                { mode: 'discharge', px: DISCHARGE_PX }, { mode: 'core', px: CORE_PX }],
       shadow: true, clips: true, cables: true, litWall: true,
       reflect: ['halo', 'discharge', 'core'], puddles: true, garland: 52, arrow: false,
       rain: 300, headlights: true }
};
const DEFAULT_TIER = 2;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function rand(a, b) { return a + Math.random() * (b - a); }
function urlParam(n) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(n);
}

// ---------------------------------------------------------------------------
// The tube. One geometry, four widths.
// ---------------------------------------------------------------------------
// Wet ground. The puddle mask is shared by the floor and by the reflected
// tubes: if the two disagreed, a reflection would sharpen where the asphalt is
// dry, which is the one thing that would give the trick away.
const WET_CHUNK = `
  float wetHash(vec2 p) {
    p = fract(p * vec2(31.31, 17.17));
    p += dot(p, p + 19.19);
    return fract(p.x * p.y);
  }
  float wetNoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(wetHash(i), wetHash(i + vec2(1.0, 0.0)), f.x),
               mix(wetHash(i + vec2(0.0, 1.0)), wetHash(i + vec2(1.0, 1.0)), f.x), f.y);
  }
  // 0 = dry matt asphalt, 1 = standing water. Two octaves, so a shoreline is
  // ragged rather than a smooth blob - a puddle edge is where the asphalt
  // happens to dip, and it is never round.
  float puddleMask(vec2 p) {
    float nn = wetNoise(p * 1.7) * 0.65 + wetNoise(p * 4.3 + 7.0) * 0.35;
    return smoothstep(0.46, 0.60, nn);
  }
`;

const tubeVertexShader = `
  precision mediump float;
  attribute vec2 aNormal;    // unit offset direction at this centreline point
  attribute float aSide;     // -1 or +1: which side of the centreline
  attribute float aAlong;    // 0..1 along this letter, electrode to electrode
  attribute float aLit;      // this letter's lit level, written per frame
  attribute float aFront;    // this letter's discharge front, written per frame
  attribute float aGas;      // 0 title, 1 artist, 2 frame

  uniform float uHalf;       // half width, world units
  uniform vec2 uOffset;      // the shadow layer's displacement
  uniform float uMirror;     // 1 = this mesh is the reflection in the wet floor
  uniform float uFloorY;     // the waterline, geometry units
  uniform float uWob;        // ripple amplitude (bands.low)
  uniform float uTime;
  // Three fills in ONE geometry. Selecting the gas per VERTEX keeps every layer
  // to a single material and a single draw call; the alternative - draw groups
  // with a cloned material per gas - triples the materials and then has to keep
  // three sets of uniforms in step every frame.
  uniform vec3 uGas0;
  uniform vec3 uGas1;
  uniform vec3 uGas2;

  varying float vRad;        // -1..1 across the tube
  varying float vOn;         // 0..1 how lit this point is
  varying vec3 vGas;
  varying float vDepth;      // how far past the waterline, 0..1
  varying vec2 vFloor;       // where on the floor this fragment lands

  void main() {
    vRad = aSide;
    vGas = aGas < 0.5 ? uGas0 : (aGas < 1.5 ? uGas1 : uGas2);
    // The discharge sweeps from one electrode to the other instead of
    // appearing along the whole length at once.
    vOn = aLit * (1.0 - smoothstep(aFront - ${PROPAGATE_SOFT.toFixed(2)}, aFront, aAlong));
    vec2 b = position.xy + aNormal * (aSide * uHalf) + uOffset;
    vDepth = 0.0;
    vFloor = b;
    if (uMirror > 0.5) {
      // Mirror about the waterline, then bend the image with two sinusoids of
      // incommensurable period so the ripple never repeats, growing with
      // distance from the wall: a reflection is sharp where it meets the
      // ground and falls apart as it comes toward you.
      float d = b.y - uFloorY;
      b.y = uFloorY - d;
      float w = sin(b.y * 11.3 + uTime * 1.70) * 0.55
              + sin(b.y * 7.10 - uTime * 1.13) * 0.45;
      vDepth = clamp(d * 1.35, 0.0, 1.0);
      b.x += w * uWob * vDepth;
      vFloor = b;
    }
    gl_Position = projectionMatrix * modelViewMatrix * vec4(b, position.z, 1.0);
  }
`;

// uMODE picks the layer at compile time - one shader source, four programs, no
// per-fragment branching on a uniform that never changes.
function tubeFragmentShader(mode) {
  return `
  precision mediump float;
  uniform vec3 uGlass;
  uniform float uIntensity;
  uniform float uHum;
  uniform float uMirror;
  uniform float uPuddles;
  varying float vRad;
  varying float vOn;
  varying vec3 vGas;
  varying float vDepth;
  varying vec2 vFloor;
  #define uGas vGas

  ${WET_CHUNK}

  void main() {
    float r = abs(vRad);
    float on = vOn * uIntensity * uHum;
    float wet = 1.0;
    if (uMirror > 0.5) {
      // 45% of the sign, fading as it comes toward the viewer, and only really
      // present where there is water to reflect in.
      float pud = mix(0.20, 1.0, puddleMask(vFloor * 2.6) * uPuddles + (1.0 - uPuddles) * 0.75);
      wet = 0.45 * (1.0 - smoothstep(0.15, 1.0, vDepth)) * pud;
      if (wet <= 0.004) discard;
    }
    ${mode === 'halo' ? `
    // Soft and wide: this is light in the air, so it has no edge at all.
    float a = pow(max(0.0, 1.0 - r), 2.6) * on * 0.27 * wet;
    if (a <= 0.002) discard;
    gl_FragColor = vec4(uGas * a, a);` : ''}
    ${mode === 'glass' ? `
    // The tube itself. NOT multiplied by "on": an unlit neon is still a glass
    // tube on a plate, and this layer is the whole reason the thing reads as a
    // sign rather than as text that happens to glow.
    float edge = smoothstep(1.0, 0.86, r);
    // A highlight down one side, where the room light catches the curve, and a
    // darker lip at the far edge: that pair is what makes it read as a
    // CYLINDER instead of a flat dark stripe.
    float hi = pow(max(0.0, 1.0 - abs(vRad + 0.45)), 6.0) * 0.5;
    float lip = smoothstep(0.55, 1.0, r) * 0.35;
    vec3 c = uGlass * (1.0 - lip) + vec3(hi);
    // Lit, the glass takes a little of the gas's colour through its own body.
    c += uGas * on * 0.22;
    gl_FragColor = vec4(c, edge * 0.95 * wet);` : ''}
    ${mode === 'discharge' ? `
    float a = pow(max(0.0, 1.0 - r), 0.75) * on * wet;
    gl_FragColor = vec4(uGas * a, a);` : ''}
    ${mode === 'core' ? `
    // Nearly white and the brightest thing on screen - this is what the shared
    // bloom is meant to catch, so it is deliberately taken to 0.95.
    float a = pow(max(0.0, 1.0 - r), 0.45) * on * wet;
    vec3 c = mix(uGas, vec3(1.0), 0.78) * 0.95;
    gl_FragColor = vec4(c * a, a);` : ''}
    ${mode === 'shadow' ? `
    // The tube's own shadow on the plate, unaffected by whether it is lit.
    float a = smoothstep(1.0, 0.7, r) * 0.55 * wet;
    gl_FragColor = vec4(vec3(0.0), a);` : ''}
    ${mode === 'fused' ? `
    // Tier 0/1 economy: one layer doing both jobs. It is the dark glass when
    // off and the gas column when lit, so criterion 1 - the tube stays visible
    // unlit - holds at every tier even where the brief drops the glass layer.
    float edge = smoothstep(1.0, 0.86, r);
    float hi = pow(max(0.0, 1.0 - abs(vRad + 0.45)), 6.0) * 0.5;
    vec3 c = mix(uGlass + vec3(hi), uGas, min(1.0, on * 1.2));
    float a = max(edge * 0.95, pow(max(0.0, 1.0 - r), 0.75) * on) * wet;
    gl_FragColor = vec4(c * max(a, 0.0), a);` : ''}
  }
`;
}

// ---------------------------------------------------------------------------
// The fittings: glass clips every 40px along the tube, and a heavier metal cap
// at each tube end. One InstancedMesh for both - a per-instance flag picks
// which, so the whole ironmongery is a single draw call.
// ---------------------------------------------------------------------------
const fittingVertexShader = `
  precision mediump float;
  attribute vec2 aOffset;
  attribute float aAngle;
  attribute float aKind;     // 0 clip, 1 electrode
  attribute float aScale;
  varying vec2 vUv;
  varying float vKind;
  void main() {
    vUv = uv;
    vKind = aKind;
    float c = cos(aAngle), s = sin(aAngle);
    vec2 p = position.xy * aScale;
    p = vec2(p.x * c - p.y * s, p.x * s + p.y * c) + aOffset;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 0.0, 1.0);
  }
`;

const fittingFragmentShader = `
  precision mediump float;
  uniform vec3 uGas;
  uniform float uIntensity;
  varying vec2 vUv;
  varying float vKind;
  void main() {
    vec2 q = (vUv - 0.5) * 2.0;
    if (vKind < 0.5) {
      // A glass clip: a small translucent block gripping the tube, picking up
      // the gas colour through its body.
      vec2 b = abs(q) - vec2(0.55, 0.92);
      float d = length(max(b, 0.0)) + min(max(b.x, b.y), 0.0);
      float a = smoothstep(0.06, -0.02, d);
      if (a <= 0.01) discard;
      vec3 c = vec3(0.020, 0.022, 0.028) + uGas * 0.18 * uIntensity;   // ~0.16
      c += pow(max(0.0, 1.0 - abs(q.x + 0.4)), 5.0) * 0.030;
      gl_FragColor = vec4(c, a * 0.62);
    } else {
      // An electrode: a thicker, darker metal cap.
      vec2 b = abs(q) - vec2(0.80, 0.80);
      float d = length(max(b, 0.0)) + min(max(b.x, b.y), 0.0);
      float a = smoothstep(0.08, -0.04, d);
      if (a <= 0.01) discard;
      // Lighter than the plate, not darker: an electrode is a metal ferrule
      // catching the tube's own light, and at 0.006 it read as a hole punched
      // through the sign.
      vec3 c = vec3(0.020, 0.020, 0.024) + vec3(0.030) * pow(max(0.0, 1.0 - abs(q.y + 0.4)), 4.0);
      c += uGas * 0.10 * uIntensity;
      gl_FragColor = vec4(c, a * 0.9);
    }
  }
`;

// The garland: one instanced quad per bulb, coloured from its own temperature
// through Ember's blackbody curve - never a white dot faded in and out.
const bulbVertexShader = `
  precision mediump float;
  attribute vec2 aOffset;
  attribute float aTemp;
  attribute float aScale;
  varying vec2 vUv;
  varying float vTemp;
  void main() {
    vUv = uv;
    vTemp = aTemp;
    vec2 p = position.xy * aScale + aOffset;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 0.0, 1.0);
  }
`;

const bulbFragmentShader = `
  precision mediump float;
  varying vec2 vUv;
  varying float vTemp;
  ${BLACKBODY_CHUNK}
  void main() {
    vec2 q = (vUv - 0.5) * 2.0;
    float r = length(q);
    float glass = smoothstep(1.0, 0.72, r);
    if (glass <= 0.01) discard;
    // The cold envelope has to be legible, or all anyone sees is the running
    // band and the garland never reads as a ring of bulbs at all. Same
    // principle as the tubes: the object exists whether or not it is lit.
    vec3 c = vec3(0.030, 0.030, 0.036) * glass;
    c += vec3(0.016, 0.012, 0.009) * pow(max(0.0, 1.0 - r * 1.6), 2.0);
    // The filament: small, hot, and the only part that blooms.
    float fil = pow(max(0.0, 1.0 - r * 1.9), 3.0);
    c += blackbody(vTemp) * (fil * 0.95 + glass * 0.18 * vTemp);
    gl_FragColor = vec4(c, max(glass * 0.55, fil * vTemp));
  }
`;

// Rain: short vertical strokes, almost transparent, that pick up the sign's
// light as they fall past it.
const rainVertexShader = `
  precision mediump float;
  attribute float aBright;
  uniform vec2 uSignHalf;
  uniform vec2 uSignCentre;
  varying float vBright;
  varying float vGlow;
  void main() {
    vBright = aBright;
    vec2 d = abs(position.xy - uSignCentre) - uSignHalf;
    float dist = length(max(d, 0.0)) + min(max(d.x, d.y), 0.0);
    // A drop only lights up while it is inside the halo, which is what makes
    // the rain belong to the sign instead of being a texture over it.
    vGlow = exp(-max(dist, 0.0) * 2.4);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const rainFragmentShader = `
  precision mediump float;
  uniform vec3 uGas;
  varying float vBright;
  varying float vGlow;
  void main() {
    float a = vBright * (0.06 + 0.55 * vGlow);
    gl_FragColor = vec4(mix(vec3(0.35), uGas, 0.65) * a, a);
  }
`;

// NO PLATE. The sign used to be tubes bolted to a painted steel panel; it is
// now tubes fixed straight to the brick, so the panel, its edge band, its
// standoffs and its drop shadow are all gone. What was the plate's rectangle
// survives only as the sign's BOUNDING BOX - the frame tube runs round it, the
// wall's light is centred on it, the garland follows it and the mains lead
// leaves from under it - which is why _plateRect is still computed and still
// carries that name.

// ---------------------------------------------------------------------------
// The wall. Without it the sign floats in an abstract black; with it there is a
// room, and the neon's light has something to fall on.
// ---------------------------------------------------------------------------
const wallFragmentShader = `
  precision mediump float;
  uniform vec2 uAspect;       // half extents of the visible frame, world units
  uniform vec2 uSignHalf;     // the plate's half extents
  uniform vec2 uSignCentre;
  uniform vec3 uGasA;
  uniform vec3 uGasB;
  uniform float uIntensity;
  uniform float uLitWall;
  uniform float uSweep;      // -2 = no car; otherwise -1..1 across the wall
  uniform float uFloorY;     // where the ground meets the wall, world units
  varying vec2 vUv;

  float h21(vec2 p) {
    p = fract(p * vec2(31.31, 17.17));
    p += dot(p, p + 19.19);
    return fract(p.x * p.y);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(h21(i), h21(i + vec2(1.0, 0.0)), f.x),
               mix(h21(i + vec2(0.0, 1.0)), h21(i + vec2(1.0, 1.0)), f.x), f.y);
  }

  void main() {
    vec2 p = (vUv - 0.5) * 2.0 * uAspect;

    // Brick, with every other course offset by half a brick.
    vec2 b = vec2(0.62, 0.24);
    float row = floor(p.y / b.y);
    float off = mod(row, 2.0) * 0.5 * b.x;
    vec2 cell = vec2(fract((p.x + off) / b.x), fract(p.y / b.y));
    float mortar = smoothstep(0.0, 0.05, cell.x) * smoothstep(1.0, 0.95, cell.x)
                 * smoothstep(0.0, 0.13, cell.y) * smoothstep(1.0, 0.87, cell.y);
    float perBrick = h21(vec2(floor((p.x + off) / b.x), row));
    // Linear. Raised from 0.0155: with the plate gone there is nothing else in
    // the frame to read as a surface, so the brick has to carry the background
    // on its own and it was too dark to see at all.
    vec3 brick = vec3(0.0340, 0.0250, 0.0225) * (0.62 + 0.75 * perBrick);
    vec3 grout = vec3(0.0255, 0.0250, 0.0245);
    vec3 col = mix(grout, brick, mortar);
    col *= 0.80 + 0.40 * vnoise(p * 9.0);

    // The sign's light on the wall: distance to the plate's rectangle, with a
    // real falloff, so the wall is brightest right behind the sign and the
    // corners of the room stay dark. This is what puts the sign IN a place.
    if (uLitWall > 0.5) {
      vec2 d = abs(p - uSignCentre) - uSignHalf;
      float dist = length(max(d, 0.0)) + min(max(d.x, d.y), 0.0);
      // Tight and weak. The long-range term that used to sit under this
      // (exp(-d*0.55), reaching the corners of the room) is gone: it tinted the
      // entire wall the dominant gas, and a brick wall lit by a sign stays a
      // brick wall except right behind the sign.
      float near = exp(-max(dist, 0.0) * 4.2);
      vec3 gas = mix(uGasA, uGasB, 0.5 + 0.5 * sin(p.y * 1.7));
      col += gas * near * uIntensity * 0.16;
    }

    // WHERE THE WALL MEETS THE GROUND. A brick wall does not stop on a ruled
    // line: it stands on a concrete plinth whose top edge is chipped and never
    // straight, the last courses are stained by splash-back, and there is a
    // band of contact shadow in the corner where almost no light reaches. That
    // corner darkening is what actually sells the join - a clean seam between
    // two flat tones reads as two rectangles meeting, which is what this was.
    {
      float hAbove = p.y - uFloorY;
      // A KERB, not a retaining wall. The first version put a plinth 0.62 units
      // tall with a ragged top and a stain band reaching 1.5 units up the
      // brick - and a dark irregular silhouette running the full width of the
      // frame, with a reflective surface under it, does not read as a skirting
      // board. It reads as a mountain ridge above a lake. Everything here is
      // now a fraction of that height, so the join is a detail at the foot of
      // the wall instead of a horizon across the middle of the picture.
      float ragged = (vnoise(vec2(p.x * 4.5, 0.0)) - 0.5) * 0.035
                   + (vnoise(vec2(p.x * 15.0, 4.0)) - 0.5) * 0.015;
      float kerbTop = 0.22 + ragged;
      float inKerb = smoothstep(kerbTop + 0.015, kerbTop - 0.015, hAbove);
      vec3 concrete = vec3(0.0115, 0.0112, 0.0108) * (0.75 + 0.5 * vnoise(p * 9.0));
      concrete += vec3(0.008) * smoothstep(kerbTop - 0.04, kerbTop, hAbove) * inKerb;
      col = mix(col, concrete, inKerb * step(0.0, hAbove));
      // The contact shadow, tight into the corner and nowhere else. No stain
      // band above it: that was the horizon.
      col *= 1.0 - 0.55 * smoothstep(0.30, 0.0, max(hAbove, 0.0));
    }

    // HEADLIGHTS. A car passes and its beam crosses the wall, throwing the
    // sign's shadow across the brick. The shadow is the sign's own rectangle
    // displaced opposite the light - which is all a hard light at a distance
    // actually does, and it costs one box distance.
    if (uSweep > -1.5) {
      float lx = uSweep * uAspect.x * 1.35;
      float beam = exp(-pow((p.x - lx) / (uAspect.x * 0.38), 2.0));
      float grazing = 0.55 + 0.45 * smoothstep(-uAspect.y, uAspect.y, p.y);
      vec2 sc = uSignCentre + vec2((p.x - lx) * 0.16, 0.0);
      vec2 sd = abs(p - sc) - uSignHalf * 1.04;
      float sdist = length(max(sd, 0.0)) + min(max(sd.x, sd.y), 0.0);
      float shadow = 1.0 - 0.88 * (1.0 - smoothstep(-0.05, 0.10, sdist));
      col += vec3(0.055, 0.052, 0.046) * beam * grazing * shadow;
    }

    // The room falls away at the edges.
    float vig = 1.0 - 0.55 * pow(length((vUv - 0.5) * 2.0) * 0.72, 2.2);
    gl_FragColor = vec4(max(col * vig, 0.0), 1.0);
  }
`;

// The wet ground: matt asphalt with standing water in it, taking the bottom of
// the frame. It also carries the sign's own glow smeared across the wet, which
// is what ties the reflection to a surface rather than leaving it floating.
const floorFragmentShader = `
  precision mediump float;
  uniform vec2 uExtent;      // half width / half height, world units
  uniform vec3 uGasA;
  uniform vec3 uGasB;
  uniform float uIntensity;
  uniform float uPuddles;
  uniform float uTime;
  uniform float uSignX;
  varying vec2 vUv;

  ${WET_CHUNK}

  void main() {
    vec2 q = (vUv - 0.5) * 2.0;             // -1..1 across the floor quad
    vec2 w = q * uExtent;
    float toward = (1.0 - vUv.y);           // 0 at the wall, 1 at the viewer

    float pud = puddleMask(w * 0.65) * uPuddles;
    // Asphalt: coarse aggregate, matt, almost no specular of its own.
    float grit = wetNoise(w * 26.0) * 0.5 + wetNoise(w * 70.0) * 0.5;
    vec3 col = vec3(0.0030, 0.0031, 0.0036) * (0.65 + 0.8 * grit);
    // Water is darker and smoother than the asphalt around it.
    col = mix(col, vec3(0.0016, 0.0018, 0.0024), pud * 0.8);

    // The sign's glow lying on the wet, stretched toward the viewer and
    // rippling. Strongest directly under the sign.
    float lat = exp(-abs(w.x - uSignX) * 0.55);
    float ripple = 0.85 + 0.15 * sin(w.y * 9.0 + uTime * 1.7 + w.x * 2.0);
    float spread = exp(-toward * 2.3) * ripple;
    vec3 gas = mix(uGasA, uGasB, 0.5 + 0.5 * sin(w.x * 1.3));
    col += gas * lat * spread * uIntensity * mix(0.10, 0.55, pud) ;

    // The floor's side of the same corner: grit and debris pile up against the
    // wall, the water there is shallow and dirty, and the corner is the darkest
    // place in the room. Only a thin wet lip actually catches the sign.
    float toWall = smoothstep(0.93, 1.0, vUv.y);
    float debris = wetNoise(vec2(w.x * 9.0, w.y * 26.0));
    col = mix(col, vec3(0.0042, 0.0039, 0.0034) * (0.6 + 0.9 * debris), toWall * 0.7);
    col *= 1.0 - 0.45 * smoothstep(0.95, 1.0, vUv.y);
    col += gas * uIntensity * 0.16 * smoothstep(0.955, 0.995, vUv.y)
         * (0.5 + 0.5 * wetNoise(vec2(w.x * 6.0, 1.0)));

    float vig = 1.0 - 0.45 * pow(abs(q.x) * 0.8, 2.0);
    gl_FragColor = vec4(max(col * vig, 0.0), 1.0);
  }
`;

const passThroughVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export class RaveSignVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._forcedText = urlParam('signtext');
    this._debugStrike = urlParam('signstrike') === '1';
    this._debugGlitch = urlParam('signglitch') === '1';
    this._forceOff = urlParam('signoff') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    // The sign IS the title and the artwork, so the DOM overlay would only
    // repeat it on top of itself. scene-manager parks the safe box far off
    // screen while this is set, and adaptive-party forwards the flag, so the
    // layout below cannot use the safe zone any more - it centres instead.
    this.hideOverlay = true;
    this._layout = TIER[this._tier];

    camera.position.set(0, 0, CAMERA_DIST);
    camera.lookAt(0, 0, 0);
    const fovRad = THREE.MathUtils.degToRad(camera.fov || 68);
    // Half the visible frame at z=0, in world units. Everything below is laid
    // out in "height units" (frame height 2) and multiplied by this, which is
    // exactly the safe zone's own convention - so the measured DOM cover rect
    // needs no conversion at all.
    this._halfH = CAMERA_DIST * Math.tan(fovRad / 2);

    this._group = new THREE.Group();
    scene.add(this._group);

    // --- the wall ---
    this._wallUniforms = {
      uAspect: { value: new THREE.Vector2(1, 1) },
      uSignHalf: { value: new THREE.Vector2(1, 0.3) },
      uSignCentre: { value: new THREE.Vector2(0, 0) },
      uGasA: { value: new THREE.Color(0xff4a1e) },
      uGasB: { value: new THREE.Color(0x2f7bff) },
      uIntensity: { value: 1 },
      uLitWall: { value: this._layout.litWall ? 1 : 0 },
      uSweep: { value: -2 },
      // WORLD units, like everything else the wall shader computes with -
      // FLOOR_TOP is in height units, and passing it raw put the kerb and the
      // contact shadow four times too high, a quarter of the way up the
      // picture instead of at the floor. That band across the middle of the
      // frame, with the wet surface below it, is what read as a mountain ridge
      // above a lake. Set properly in _syncFloorUniform once _halfH is known.
      uFloorY: { value: FLOOR_TOP }
    };
    this._wall = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.ShaderMaterial({
        uniforms: this._wallUniforms,
        vertexShader: passThroughVertexShader,
        fragmentShader: wallFragmentShader,
        depthTest: false, depthWrite: false
      })
    );
    this._wall.frustumCulled = false;
    this._wall.renderOrder = -10;
    this._wallUniforms.uFloorY.value = FLOOR_TOP * this._halfH;   // -> world
    this._group.add(this._wall);

    // --- the wet floor ---
    this._floorUniforms = {
      uExtent: { value: new THREE.Vector2(1, 1) },
      uGasA: { value: new THREE.Color(0xff4a1e) },
      uGasB: { value: new THREE.Color(0x2f7bff) },
      uIntensity: { value: 1 },
      uPuddles: { value: this._layout.puddles ? 1 : 0 },
      uTime: { value: 0 },
      uSignX: { value: 0 }
    };
    this._floor = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.ShaderMaterial({
        uniforms: this._floorUniforms,
        vertexShader: passThroughVertexShader,
        fragmentShader: floorFragmentShader,
        depthTest: false, depthWrite: false
      })
    );
    this._floor.frustumCulled = false;
    this._floor.renderOrder = -9.5;
    this._floor.visible = this._layout.reflect.length > 0;
    this._group.add(this._floor);

    // --- the tube layers, all sharing one geometry ---
    this._geometry = null;
    this._layerMeshes = [];
    this._gasTitle = new THREE.Color(0xff4a1e);
    this._gasArtist = new THREE.Color(0x2f7bff);
    this._gasFrame = new THREE.Color(0xb060ff);

    // --- fittings and cables ---
    this._fittings = null;
    this._cables = null;

    // --- state ---
    this._time = 0;
    this._paused = false;
    this._letters = [];
    this._textKey = null;
    this._lastTrackId = undefined;
    this._lastPaletteRef = undefined;
    this._lastBeatIndex = null;
    this._lastBarIndex = null;
    this._lastPhraseIndex = null;
    this._beatCursor = 0;
    this._barPeakUntil = -1;
    this._sizzleUntil = -1;
    this._sizzleNext = 0;
    this._chaseUntil = -1;
    this._humPhase = 0;
    this._failIndex = -1;
    this._failNextAt = 0;
    this._litFraction = PROGRESS_LIT_MIN;
    this._lastSafeKey = '';
    this._px = 1 / 540;   // world units per screen pixel, set in _syncPixel
    this._garland = null;
    this._rain = null;
    this._arrowStart = -1;
    this._bulbCount = 0;
    this._chasePos = 0;
    this._chaseDir = 1;
    this._sweep = -2;
    this._sweepT0 = -1;
    this._nextHeadlightAt = rand(HEADLIGHT_MIN_S, HEADLIGHT_MAX_S);
    this._camX = 0;
    this._syncPixel();
    this._buildRain();
  }

  // One screen pixel, in the GEOMETRY's own units - the frame is 2 tall there,
  // and the tube meshes are scaled to world by _halfH afterwards. Getting this
  // in world units instead was the bug that made every tube _halfH times too
  // thick: the width went through the scale twice and 9px glass came out as
  // 36px blocks. The buffer height, not the CSS height: the renderer's buffer
  // is scaled by tier and dpr.
  _syncPixel() {
    const h = Math.max(120, getRenderBufferHeight());
    this._px = 2 / h;
  }

  // --- text ------------------------------------------------------------------
  _pickText(profile) {
    if (this._forcedText) return this._forcedText;
    // A sign with nothing on it is not a sign, so this cascades rather than
    // rendering an empty plate: title, artist, album, and finally a word that
    // is at least honest about what the thing is.
    const t = cleanDisplayTitle(profile.title);
    if (t && t.trim()) return t;
    const a = cleanDisplayArtist(profile.artist);
    if (a && a.trim()) return a;
    if (profile.album && String(profile.album).trim()) return String(profile.album);
    return 'ON AIR';
  }

  // Upper case only, and only the characters the font actually carries.
  //
  // ACCENTS: a Hershey face is ASCII, so "CAFÉ" used to come out as "CAF?".
  // Decomposing to NFD and dropping the combining marks folds every accented
  // Latin letter onto its base in one step - E-acute becomes E, C-cedilla
  // becomes C - which is what a sign shop would bend anyway. The handful of
  // letters that do NOT decompose (the slashed O, the ligatures, the German
  // sharp s) have no base to fall back to and are spelled out instead.
  _sanitise(s, font) {
    const out = [];
    const folded = String(s)
      .replace(/[\u00C6\u00E6]/g, 'AE')
      .replace(/[\u0152\u0153]/g, 'OE')
      .replace(/[\u00D8\u00F8]/g, 'O')
      .replace(/[\u00D0\u00F0\u0110\u0111]/g, 'D')
      .replace(/[\u00DE\u00FE]/g, 'TH')
      .replace(/[\u0141\u0142]/g, 'L')
      .replace(/\u00DF/g, 'SS')
      .replace(/[\u2018\u2019\u201B]/g, "'")
      .replace(/[\u201C\u201D]/g, '"')
      .replace(/[\u2013\u2014\u2212]/g, '-')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const up = folded.toUpperCase();
    for (let i = 0; i < up.length; i++) {
      const c = up[i];
      if (c === ' ') { out.push(' '); continue; }
      if (font.glyphs[c]) out.push(c);
      else if (font.glyphs['?']) out.push('?');
    }
    return out.join('').replace(/\s+/g, ' ').trim();
  }

  _measure(font, text) {
    let w = 0;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (c === ' ') { w += WORD_SPACE + TRACK_SPACING; continue; }
      w += (font.advances[c] != null ? font.advances[c] : 0.7) + TRACK_SPACING;
    }
    return Math.max(0.001, w - TRACK_SPACING);
  }

  // Two lines when the title is long, broken at the space nearest the middle so
  // neither line is a stub. Never truncated - the brief is explicit, and a sign
  // with half a word on it is worse than a small sign.
  _wrap(text) {
    if (text.length <= MAX_LINE_CHARS) return [text];
    const mid = text.length / 2;
    let best = -1, bestD = 1e9;
    for (let i = 0; i < text.length; i++) {
      if (text[i] !== ' ') continue;
      const d = Math.abs(i - mid);
      if (d < bestD) { bestD = d; best = i; }
    }
    if (best < 0) return [text];
    return [text.slice(0, best).trim(), text.slice(best + 1).trim()];
  }

  // ---------------------------------------------------------------------------
  // Geometry. Builds the centreline strip for every letter of every line, plus
  // the decorative frame, into ONE BufferGeometry that all four layers share.
  // ---------------------------------------------------------------------------
  _build(font, profile) {
    const pos = [], nrm = [], side = [], along = [], lit = [], front = [], gasA = [];
    const letters = [];
    const clips = [], electrodes = [];

    // --- work out the band the sign may occupy -------------------------------
    // Measured from the safe zone, never hard-coded: the DOM cover sits in the
    // middle of the screen and the sign has to live above it. The band is
    // whatever is left between the top of that rect and the top of the frame.
    // With the overlay hidden there is no cover to dodge, so the sign takes the
    // whole wall above the wet floor and is centred in it. (The safe zone is
    // parked off screen in this mode and must not be read.)
    const bandTop = 0.93;
    // Clear of the ground by about an eighth of the frame height. The plate
    // used to stop exactly on the waterline, so the object and its reflection
    // touched - and it is the GAP between them that reads as depth.
    const bandBottom = FLOOR_TOP + PLATE_LIFT;
    const bandH = Math.max(0.16, bandTop - bandBottom);
    const maxW = 1.76 * (safeZoneUniforms.uAspect.value || 1.6);   // full width, height units

    const titleText = this._textLines;
    const artistText = this._artistLine;

    // Height of the whole block in cap heights: title lines + artist + gaps.
    const nLines = titleText.length;
    // The PLATE's margin is part of the block: the band has to hold the steel,
    // not just the glass, or the sign grows off the top of the frame.
    const blockCaps = nLines + (nLines - 1) * (LINE_GAP - 1)
      + (artistText ? LINE_GAP * 0.92 * ARTIST_SCALE + 0.55 : 0)
      + 2 * PLATE_MARGIN;

    // Fit to the band's height, then to its width, and take the smaller - which
    // is how a long title gets smaller instead of getting cut.
    let cap = bandH / Math.max(blockCaps, 0.001);
    let widest = 0;
    for (const l of titleText) widest = Math.max(widest, this._measure(font, l));
    if (artistText) widest = Math.max(widest, this._measure(font, artistText) * ARTIST_SCALE);
    const availW = maxW - 2 * PLATE_MARGIN * 0.5;
    if (widest * cap > availW) cap = availW / widest;
    // And the PLATE - text plus its margin - may not exceed 60% of the frame's
    // width. Without this a long title pushed the panel to 82% and there was no
    // wall left around it to say the thing is hanging on one.
    const aspectW = safeZoneUniforms.uAspect.value || 1.6;
    const capForPlate = (PLATE_MAX_FRAC * aspectW) / Math.max(widest * 0.5 + PLATE_MARGIN, 0.001);
    cap = Math.min(cap, capForPlate);
    cap = Math.max(0.035, cap);

    const H = this._halfH;
    const px = this._px;

    // --- lay the lines out ---------------------------------------------------
    // Cap the height and sit the sign just above the waterline. A reflection
    // only shows what is within (waterline - frame bottom) of the water, so a
    // sign centred in the whole band reflects only its bottom edge - which
    // wastes the most expensive thing in the file.
    const maxBlockH = (FLOOR_TOP + 1.0) * 1.18;
    if (blockCaps * cap > maxBlockH) cap = maxBlockH / blockCaps;
    const blockH = blockCaps * cap;
    const rows = [];
    let y = bandBottom + blockH - PLATE_MARGIN * cap - cap;
    for (const line of titleText) {
      rows.push({ text: line, cap: cap, y: y, gas: 0 });
      y -= cap * LINE_GAP;
    }
    if (artistText) {
      y -= cap * 0.22;
      rows.push({ text: artistText, cap: cap * ARTIST_SCALE, y: y, gas: 1 });
      y -= cap * ARTIST_SCALE * LINE_GAP;
    }

    // Bounds of the drawn text, for the plate and the frame.
    let minX = 1e9, maxX = -1e9, minY = 1e9, maxY = -1e9;
    for (const row of rows) {
      const w = this._measure(font, row.text) * row.cap;
      row.x0 = -w * 0.5;
      minX = Math.min(minX, row.x0);
      maxX = Math.max(maxX, row.x0 + w);
      minY = Math.min(minY, row.y);
      maxY = Math.max(maxY, row.y + row.cap);
    }

    // The block estimate above is close but not exact - the artist line's own
    // leading does not fall out of it cleanly - so the rows are SHIFTED by the
    // measured error rather than trusted. That puts the plate's bottom edge
    // exactly on the lift, every time, whatever the text does.
    const plateHalfX0 = (maxX - minX) * 0.5 + PLATE_MARGIN * cap;
    const plateHalfY = (maxY - minY) * 0.5 + PLATE_MARGIN * cap;
    const shift = bandBottom - (minY - PLATE_MARGIN * cap);
    if (shift !== 0) {
      for (const row of rows) row.y += shift;
      minY += shift; maxY += shift;
    }
    const plateHalfX = plateHalfX0;
    const plateCx = (minX + maxX) * 0.5;
    const plateCy = (minY + maxY) * 0.5;

    // --- the decorative frame, in tube ---------------------------------------
    const fr = plateHalfX - 0.10 * cap;
    const ft = plateHalfY - 0.10 * cap;
    const rr = Math.min(0.35 * cap, Math.min(fr, ft) * 0.5);
    const frameStrokes = [this._roundedRect(plateCx, plateCy, fr, ft, rr)];

    // --- emit every letter ---------------------------------------------------
    const emitLetter = (strokes, gasIndex, isFrame, rowScale) => {
      const start = pos.length / 3;
      // Arc length across the WHOLE letter, so the discharge crosses it as one
      // tube rather than restarting on each pen stroke.
      let total = 0;
      const lens = [];
      for (const s of strokes) {
        let l = 0;
        for (let i = 1; i < s.length; i++) l += Math.hypot(s[i][0] - s[i - 1][0], s[i][1] - s[i - 1][1]);
        lens.push(l); total += l;
      }
      if (total <= 0) return null;

      let acc = 0;
      for (let si = 0; si < strokes.length; si++) {
        const p = strokes[si];
        const n = p.length;
        const arc = [acc];
        for (let i = 1; i < n; i++) {
          acc += Math.hypot(p[i][0] - p[i - 1][0], p[i][1] - p[i - 1][1]);
          arc.push(acc);
        }
        // Averaged tangents so adjacent segments share an offset direction and
        // the tube stays continuous round a bend.
        const nor = [];
        for (let i = 0; i < n; i++) {
          const a = p[Math.max(0, i - 1)], b = p[Math.min(n - 1, i + 1)];
          let dx = b[0] - a[0], dy = b[1] - a[1];
          const L = Math.hypot(dx, dy) || 1;
          nor.push([-dy / L, dx / L]);
        }
        for (let i = 0; i < n - 1; i++) {
          const a = p[i], b = p[i + 1], na = nor[i], nb = nor[i + 1];
          const aa = arc[i] / total, ab = arc[i + 1] / total;
          const v = [
            [a, na, aa, 1], [b, nb, ab, 1], [a, na, aa, -1],
            [b, nb, ab, 1], [b, nb, ab, -1], [a, na, aa, -1]
          ];
          for (const q of v) {
            pos.push(q[0][0], q[0][1], 0);
            nrm.push(q[1][0], q[1][1]);
            along.push(q[2]);
            side.push(q[3]);
            lit.push(0); front.push(1); gasA.push(gasIndex);
          }
        }
        // Fittings: a clip every CLIP_SPACING_PX along the path, and an
        // electrode cap at each end of each stroke.
        if (this._layout.clips && !isFrame) {
          // March along the stroke placing a clip every CLIP_SPACING_PX. The
          // distance walked has to be measured from the START OF THIS STROKE,
          // not from the letter-wide accumulator - mixing the two put a clip on
          // almost every vertex.
          // The SPACING stays absolute - a clip every 40px of tube, whatever
          // the letter's size - so small letters get proportionally fewer of
          // them, not more. Only the clip's own size follows the row.
          const step = CLIP_SPACING_PX * px;
          let walked = 0;
          let next = step * 0.5;
          for (let i = 1; i < n; i++) {
            const segLen = Math.hypot(p[i][0] - p[i - 1][0], p[i][1] - p[i - 1][1]);
            if (segLen < 1e-6) continue;
            while (next <= walked + segLen) {
              const t = (next - walked) / segLen;
              clips.push({
                x: lerp(p[i - 1][0], p[i][0], t),
                y: lerp(p[i - 1][1], p[i][1], t),
                a: Math.atan2(p[i][1] - p[i - 1][1], p[i][0] - p[i - 1][0]),
                s: rowScale || 1
              });
              next += step;
            }
            walked += segLen;
          }
        }
      }
      // The two ends of this letter's tube run, remembered so the caller can
      // cap a WORD rather than a letter (see below).
      let ends = null;
      if (strokes.length) {
        const first = strokes[0], last = strokes[strokes.length - 1];
        const lp = last[last.length - 1], lq = last[last.length - 2];
        ends = {
          a: { x: first[0][0], y: first[0][1],
               a: Math.atan2(first[1][1] - first[0][1], first[1][0] - first[0][0]),
               s: rowScale || 1 },
          b: { x: lp[0], y: lp[1],
               a: Math.atan2(lp[1] - lq[1], lp[0] - lq[0]),
               s: rowScale || 1 }
        };
      }

      const count = pos.length / 3 - start;
      const rec = { start: start, count: count, gas: gasIndex, frame: !!isFrame,
        lit: 0, front: 1, state: 'off', pulses: null, t0: 0, dur: 0, want: false,
        ends: ends };
      letters.push(rec);
      return rec;
    };

    // Letters of every row.
    const titleCap = rows.length ? rows[0].cap : 1;
    for (const row of rows) {
      let x = row.x0;
      // ELECTRODES PER WORD, not per letter. A word is one tube run in a real
      // sign, and capping every letter put twenty metal ferrules across a ten
      // letter line - which on the artist row, drawn at half the title's size,
      // read as a scatter of squares over the glyphs rather than as hardware.
      let wordFirst = null, wordLast = null;
      const capWord = () => {
        if (this._layout.clips && wordFirst && wordFirst.ends && wordLast && wordLast.ends) {
          electrodes.push(wordFirst.ends.a);
          electrodes.push(wordLast.ends.b);
        }
        wordFirst = wordLast = null;
      };
      for (let i = 0; i < row.text.length; i++) {
        const ch = row.text[i];
        if (ch === ' ') { capWord(); x += (WORD_SPACE + TRACK_SPACING) * row.cap; continue; }
        const g = font.glyphs[ch];
        const adv = font.advances[ch] != null ? font.advances[ch] : 0.7;
        if (g && g.length) {
          const placed = g.map(s => s.map(pt => [x + pt[0] * row.cap, row.y + pt[1] * row.cap]));
          // The fittings follow the row's own size, so a small line gets small
          // hardware instead of the title's.
          const rec = emitLetter(placed, row.gas, false, row.cap / titleCap);
          if (!wordFirst) wordFirst = rec;
          wordLast = rec;
        }
        x += (adv + TRACK_SPACING) * row.cap;
      }
      capWord();
    }
    // The frame is one more "letter" in the same geometry - it lights and
    // strikes exactly like the rest, which is what lets the phrase chase run
    // through the same machinery.
    this._frameLetterStart = letters.length;
    for (const s of frameStrokes) {
      const rec = emitLetter([s], 2, true, 1);
      if (this._layout.clips && rec && rec.ends) {
        electrodes.push(rec.ends.a); electrodes.push(rec.ends.b);
      }
    }

    // --- upload --------------------------------------------------------------
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('aNormal', new THREE.Float32BufferAttribute(nrm, 2));
    geo.setAttribute('aSide', new THREE.Float32BufferAttribute(side, 1));
    geo.setAttribute('aAlong', new THREE.Float32BufferAttribute(along, 1));
    geo.setAttribute('aGas', new THREE.Float32BufferAttribute(gasA, 1));
    this._litAttr = new THREE.Float32BufferAttribute(new Float32Array(lit), 1);
    this._frontAttr = new THREE.Float32BufferAttribute(new Float32Array(front), 1);
    this._litAttr.setUsage(THREE.DynamicDrawUsage);
    this._frontAttr.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aLit', this._litAttr);
    geo.setAttribute('aFront', this._frontAttr);

    this._disposeTubes();
    this._geometry = geo;
    this._letters = letters;

    // --- the layers, all on the same geometry --------------------------------
    // The shadow goes down first so everything else lands on top of it.
    if (this._layout.shadow) {
      this._addLayer('shadow', GLASS_PX, -20,
        new THREE.Vector2(SHADOW_PX * px, -SHADOW_PX * px), THREE.NormalBlending);
    }
    let order = -8;
    for (const spec of this._layout.layers) {
      const opaque = (spec.mode === 'glass' || spec.mode === 'fused');
      this._addLayer(spec.mode, spec.px, order++, new THREE.Vector2(0, 0),
        opaque ? THREE.NormalBlending : THREE.AdditiveBlending, false);
    }
    // THE REFLECTION. The same geometry again, mirrored about the waterline in
    // the vertex shader - no render target and no second camera, which is what
    // keeps this affordable. Only the LIT layers are worth reflecting: dark
    // glass reflects nothing anybody can see.
    for (const spec of this._layout.layers) {
      if (this._layout.reflect.indexOf(spec.mode) < 0) continue;
      const opaque = (spec.mode === 'glass' || spec.mode === 'fused');
      this._addLayer(spec.mode, spec.px, order++, new THREE.Vector2(0, 0),
        opaque ? THREE.NormalBlending : THREE.AdditiveBlending, true);
    }

    // --- the garland, round the frame ---------------------------------------
    this._buildGarland(frameStrokes[0]);
    // --- the arrow, pointing at the title ------------------------------------
    this._buildArrow(emitLetter, plateCx, plateCy, plateHalfX, plateHalfY, cap);

    // Recorded BEFORE the cables are built: the mains lead leaves the plate's
    // bottom right corner and cannot be routed without knowing where that is.
    this._plateRect = { cx: plateCx, cy: plateCy, hx: plateHalfX, hy: plateHalfY };

    // --- fittings ------------------------------------------------------------
    this._buildFittings(clips, electrodes, px);
    // --- cables --------------------------------------------------------------
    if (this._layout.cables) this._buildCables();

    // --- where the sign sits, for the wall light and the rain ---------------
    this._wallUniforms.uSignHalf.value.set(plateHalfX * H, plateHalfY * H);
    this._wallUniforms.uSignCentre.value.set(plateCx * H, plateCy * H);
    this._group.scale.set(1, 1, 1);
    this._signHalfY = plateHalfY;

    // One letter of the title is faulty, chosen once per track and then left
    // alone. Never the frame, and never a space.
    const body = letters.filter(l => !l.frame);
    this._failIndex = body.length ? letters.indexOf(body[Math.floor(Math.random() * body.length)]) : -1;
    this._failNextAt = this._time + rand(FAIL_MIN_S, FAIL_MAX_S);
    this._beatCursor = 0;
  }

  // Bulbs at equal arc positions round the frame's own polyline, so the
  // garland follows the moulding exactly instead of being a second rectangle
  // that has to be kept in step with it.
  _buildGarland(outline) {
    const count = this._layout.garland;
    const H = this._halfH;
    let total = 0;
    const seg = [];
    for (let i = 1; i < outline.length; i++) {
      const d = Math.hypot(outline[i][0] - outline[i - 1][0], outline[i][1] - outline[i - 1][1]);
      seg.push(d); total += d;
    }
    const pos = new Float32Array(count * 2);
    const scl = new Float32Array(count);
    const bulbPx = GLASS_PX * this._px * H * 1.35;
    let want = 0, walked = 0, k = 0;
    for (let i = 0; i < seg.length && k < count; i++) {
      while (k < count && want <= walked + seg[i]) {
        const t = seg[i] > 1e-9 ? (want - walked) / seg[i] : 0;
        pos[k * 2] = lerp(outline[i][0], outline[i + 1][0], t) * H;
        pos[k * 2 + 1] = lerp(outline[i][1], outline[i + 1][1], t) * H;
        scl[k] = bulbPx;
        k++;
        want += total / count;
      }
      walked += seg[i];
    }
    for (; k < count; k++) { pos[k * 2] = 0; pos[k * 2 + 1] = 0; scl[k] = 0; }

    const base = new THREE.PlaneGeometry(1, 1);
    const geo = new THREE.InstancedBufferGeometry();
    geo.index = base.index;
    geo.attributes.position = base.attributes.position;
    geo.attributes.uv = base.attributes.uv;
    geo.setAttribute('aOffset', new THREE.InstancedBufferAttribute(pos, 2));
    geo.setAttribute('aScale', new THREE.InstancedBufferAttribute(scl, 1));
    this._bulbTemp = new THREE.InstancedBufferAttribute(new Float32Array(count), 1);
    this._bulbTemp.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aTemp', this._bulbTemp);
    geo.instanceCount = count;
    const mesh = new THREE.Mesh(geo, new THREE.ShaderMaterial({
      vertexShader: bulbVertexShader, fragmentShader: bulbFragmentShader,
      transparent: true, blending: THREE.AdditiveBlending,
      depthTest: false, depthWrite: false
    }));
    mesh.frustumCulled = false;
    mesh.renderOrder = -4;
    this._group.add(mesh);
    this._garland = mesh;
    this._garlandBase = base;
    this._bulbCount = count;
    this._chasePos = 0;
    this._chaseDir = 1;
  }

  // An arrow beside the sign, pointing at the title, whose segments light in
  // sequence. Placed on whichever side has room; on a title wide enough to
  // leave none it is simply left off rather than drawn over the frame.
  // DISABLED. The arrow sat beside the plate, attached to nothing, and its
  // segments light in sequence - so at any instant it read as a handful of
  // floating dashes rather than as an arrow. Nothing on this wall should be
  // detached from the sign. Kept rather than deleted because the geometry is
  // sound and it would work if it were ever mounted ON the plate.
  _buildArrow(emitLetter, cx, cy, hx, hy, cap) {
    this._arrowStart = -1;
    if (!this._layout.arrow) return;
    const aspect = safeZoneUniforms.uAspect.value || 1.6;
    const len = cap * 1.5;
    const gap = cap * 0.55;
    const leftX = cx - hx - gap - len;
    const rightX = cx + hx + gap;
    let x0, dir;
    if (leftX > -aspect + 0.05) { x0 = leftX; dir = 1; }
    else if (rightX + len < aspect - 0.05) { x0 = rightX + len; dir = -1; }
    else return;                        // no room: no arrow
    const y0 = cy;
    const h = cap * 0.42;
    this._arrowStart = this._letters.length;
    // Three shaft chunks, then the two barbs of the head.
    const shaft = len * 0.62;
    for (let i = 0; i < 3; i++) {
      const a = x0 + dir * (shaft * i / 3);
      const b = x0 + dir * (shaft * (i + 1) / 3);
      emitLetter([[[a, y0], [b, y0]]], 2, true);
    }
    const tip = x0 + dir * len;
    const back = x0 + dir * (len - h);
    emitLetter([[[back, y0 + h], [tip, y0]]], 2, true);
    emitLetter([[[back, y0 - h], [tip, y0]]], 2, true);
    this._arrowCount = ARROW_SEGMENTS;
  }

  // Rain: short vertical strokes as one LineSegments, updated on the CPU. Two
  // vertices a drop, 300 drops - the upload is smaller than a single texture
  // row and it keeps the whole thing to one draw call.
  _buildRain() {
    const count = this._layout.rain;
    if (!count) { this._rain = null; return; }
    const H = this._halfH;
    const aspect = safeZoneUniforms.uAspect.value || 1.6;
    this._rainCount = count;
    this._rainX = new Float32Array(count);
    this._rainY = new Float32Array(count);
    this._rainV = new Float32Array(count);
    this._rainL = new Float32Array(count);
    const pos = new Float32Array(count * 6);
    const bright = new Float32Array(count * 2);
    for (let i = 0; i < count; i++) {
      this._rainX[i] = (Math.random() * 2 - 1) * aspect * H;
      this._rainY[i] = (Math.random() * 2 - 1) * H;
      this._rainV[i] = rand(RAIN_SPEED_MIN, RAIN_SPEED_MAX) * H;
      this._rainL[i] = rand(RAIN_LEN_MIN, RAIN_LEN_MAX) * H;
      const b = rand(0.35, 1.0);
      bright[i * 2] = b; bright[i * 2 + 1] = b * 0.25;
    }
    const geo = new THREE.BufferGeometry();
    this._rainPos = new THREE.BufferAttribute(pos, 3);
    this._rainPos.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('position', this._rainPos);
    geo.setAttribute('aBright', new THREE.BufferAttribute(bright, 1));
    this._rainUniforms = {
      uSignHalf: { value: new THREE.Vector2(1, 0.3) },
      uSignCentre: { value: new THREE.Vector2(0, 0) },
      uGas: { value: new THREE.Color(0xffffff) }
    };
    const mesh = new THREE.LineSegments(geo, new THREE.ShaderMaterial({
      uniforms: this._rainUniforms,
      vertexShader: rainVertexShader, fragmentShader: rainFragmentShader,
      transparent: true, blending: THREE.AdditiveBlending,
      depthTest: false, depthWrite: false
    }));
    mesh.frustumCulled = false;
    mesh.renderOrder = -3;     // in front of the sign: it falls between us and it
    this._group.add(mesh);
    this._rain = mesh;
  }

  _roundedRect(cx, cy, hx, hy, r) {
    const pts = [];
    const seg = 5;
    const corner = (ox, oy, a0, a1) => {
      for (let i = 0; i <= seg; i++) {
        const a = lerp(a0, a1, i / seg);
        pts.push([cx + ox + Math.cos(a) * r, cy + oy + Math.sin(a) * r]);
      }
    };
    pts.push([cx - hx + r, cy - hy]);
    pts.push([cx + hx - r, cy - hy]);
    corner(hx - r, -hy + r, -Math.PI / 2, 0);
    pts.push([cx + hx, cy + hy - r]);
    corner(hx - r, hy - r, 0, Math.PI / 2);
    pts.push([cx - hx + r, cy + hy]);
    corner(-hx + r, hy - r, Math.PI / 2, Math.PI);
    pts.push([cx - hx, cy - hy + r]);
    corner(-hx + r, -hy + r, Math.PI, Math.PI * 1.5);
    return pts;
  }

  _addLayer(mode, widthPx, order, offset, blending, mirror) {
    const H = this._halfH;
    const uniforms = {
      // Both in geometry units: they are added to `position` before the mesh's
      // own scale takes the result to world.
      uHalf: { value: widthPx * this._px * 0.5 },
      uOffset: { value: new THREE.Vector2(offset.x, offset.y) },
      uGas0: { value: this._gasTitle },
      uGas1: { value: this._gasArtist },
      uGas2: { value: this._gasFrame },
      // Set from a LINEAR triple, not a hex: THREE.Color.set(hex) treats the
      // hex as sRGB and converts, which would land this at 0.005 - invisible.
      // The tube wants to read around 0.17 on screen against a 0.07 plate.
      uGlass: { value: new THREE.Color().setRGB(0.0200, 0.0215, 0.0265, THREE.LinearSRGBColorSpace) },
      uIntensity: { value: 1 },
      uHum: { value: 1 },
      uMirror: { value: mirror ? 1 : 0 },
      uFloorY: { value: FLOOR_TOP },
      uWob: { value: 0.02 },
      uPuddles: { value: this._layout.puddles ? 1 : 0 },
      uTime: { value: 0 }
    };
    const mesh = new THREE.Mesh(this._geometry, new THREE.ShaderMaterial({
      uniforms: uniforms,
      vertexShader: tubeVertexShader,
      fragmentShader: tubeFragmentShader(mode),
      transparent: true,
      blending: blending,
      // DoubleSide, and not optional. The strip's two triangles are emitted as
      // (a+, b+, a-) and (b+, b-, a-), whose winding flips with the direction
      // the stroke happens to travel - so with the default FrontSide most of
      // the sign was silently back-face culled and only the few segments that
      // doubled back ever rasterised. A flat ribbon has no meaningful facing.
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false
    }));
    mesh.frustumCulled = false;
    mesh.renderOrder = order;
    mesh.scale.set(this._halfH, this._halfH, 1);
    mesh.userData.mode = mode;
    mesh.userData.widthPx = widthPx;
    mesh.userData.mirror = !!mirror;
    this._group.add(mesh);
    this._layerMeshes.push(mesh);
  }

  _buildFittings(clips, electrodes, px) {
    const total = clips.length + electrodes.length;
    if (!total) { this._fittings = null; return; }
    const H = this._halfH;
    const base = new THREE.PlaneGeometry(1, 1);
    const geo = new THREE.InstancedBufferGeometry();
    geo.index = base.index;
    geo.attributes.position = base.attributes.position;
    geo.attributes.uv = base.attributes.uv;
    const off = new Float32Array(total * 2);
    const ang = new Float32Array(total);
    const kind = new Float32Array(total);
    const scl = new Float32Array(total);
    let k = 0;
    for (const c of clips) {
      off[k * 2] = c.x * H; off[k * 2 + 1] = c.y * H;
      // The clip grips ACROSS the tube, so it is rotated a quarter turn from
      // the direction of travel.
      ang[k] = c.a + Math.PI / 2;
      kind[k] = 0; scl[k] = GLASS_PX * px * H * 1.15 * (c.s || 1); k++;
    }
    for (const e of electrodes) {
      off[k * 2] = e.x * H; off[k * 2 + 1] = e.y * H;
      ang[k] = e.a; kind[k] = 1; scl[k] = GLASS_PX * px * H * 1.30 * (e.s || 1); k++;
    }
    geo.setAttribute('aOffset', new THREE.InstancedBufferAttribute(off, 2));
    geo.setAttribute('aAngle', new THREE.InstancedBufferAttribute(ang, 1));
    geo.setAttribute('aKind', new THREE.InstancedBufferAttribute(kind, 1));
    geo.setAttribute('aScale', new THREE.InstancedBufferAttribute(scl, 1));
    geo.instanceCount = total;
    this._fittingUniforms = {
      uGas: { value: new THREE.Color(0xff4a1e) },
      uIntensity: { value: 1 }
    };
    const mesh = new THREE.Mesh(geo, new THREE.ShaderMaterial({
      uniforms: this._fittingUniforms,
      vertexShader: fittingVertexShader,
      fragmentShader: fittingFragmentShader,
      transparent: true, depthTest: false, depthWrite: false
    }));
    mesh.frustumCulled = false;
    mesh.renderOrder = -5;
    this._group.add(mesh);
    this._fittings = mesh;
    this._fittingBase = base;
  }

  // ONE cable. The previous version wired every letter's electrode down to a
  // bus and then ran the bus sideways, which drew a diagonal across the picture
  // to the frame's edge and left hardware floating clear of the plate. A sign's
  // own wiring is behind the steel; the only thing you see is the lead going
  // down to the ground.
  _buildCables() {
    if (!this._plateRect) { this._cables = null; return; }
    const H = this._halfH;
    const r0 = this._plateRect;
    const x = r0.cx + r0.hx * 0.62;
    const y0 = r0.cy - r0.hy;
    const pts = [];
    // Straight down from under the plate and out of the bottom of the frame,
    // with a slight sag on the way so it reads as a cable and not a ruled line.
    let px0 = x, py0 = y0;
    for (let i = 1; i <= 8; i++) {
      const t = i / 8;
      const yy = lerp(y0, -1.08, t);
      const xx = x + Math.sin(t * Math.PI) * 0.012;
      pts.push(px0 * H, py0 * H, 0.02, xx * H, yy * H, 0.02);
      px0 = xx; py0 = yy;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    const mat = new THREE.LineBasicMaterial({
      color: 0x05050a, transparent: true, opacity: 0.75, depthTest: false, depthWrite: false
    });
    const lines = new THREE.LineSegments(geo, mat);
    lines.frustumCulled = false;
    lines.renderOrder = -8;
    this._group.add(lines);
    this._cables = lines;
  }

  _disposeTubes() {
    if (this._garland) {
      this._group.remove(this._garland);
      this._garland.geometry.dispose();
      this._garland.material.dispose();
      this._garland = null;
    }
    if (this._garlandBase) { this._garlandBase.dispose(); this._garlandBase = null; }
    for (const m of this._layerMeshes) {
      this._group.remove(m);
      m.material.dispose();
    }
    this._layerMeshes.length = 0;
    if (this._geometry) { this._geometry.dispose(); this._geometry = null; }
    if (this._fittings) {
      this._group.remove(this._fittings);
      this._fittings.geometry.dispose();
      this._fittings.material.dispose();
      this._fittings = null;
    }
    if (this._fittingBase) { this._fittingBase.dispose(); this._fittingBase = null; }
    if (this._cables) {
      this._group.remove(this._cables);
      this._cables.geometry.dispose();
      this._cables.material.dispose();
      this._cables = null;
    }
  }

  _disposeRain() {
    if (!this._rain) return;
    this._group.remove(this._rain);
    this._rain.geometry.dispose();
    this._rain.material.dispose();
    this._rain = null;
  }

  // --- gases -----------------------------------------------------------------
  // The artwork chooses the hue, physics chooses which hue is available: each
  // palette colour is pulled onto the nearest real gas fill and its saturation
  // forced up, so the sign can never come out in a pastel no tube has ever
  // glowed in.
  _applyPalette(palette) {
    const vivid = (palette && palette.vividColors && palette.vividColors.length)
      ? palette.vividColors : [0xff4a1e, 0x2f7bff, 0xb060ff];
    const hsl = { h: 0, s: 0, l: 0 };
    const tmp = new THREE.Color();
    const pick = (i, target) => {
      tmp.set(vivid[i % vivid.length]);
      tmp.getHSL(hsl);
      let best = GAS_HUES[0], bd = 9;
      for (const g of GAS_HUES) {
        let d = Math.abs(g - hsl.h);
        d = Math.min(d, 1 - d);
        if (d < bd) { bd = d; best = g; }
      }
      target.setHSL(best, Math.max(GAS_SAT_MIN, hsl.s), 0.55);
    };
    // Three DIFFERENT fills, as a sign shop would have. Claiming two are
    // distinct because their hex integers differ is not enough - ff9259 and
    // fd9561 are the same orange to anyone looking at the sign - so the test is
    // on HUE, circularly, and a clash is pushed to the next gas in the list.
    const used = [];
    const assign = (i, target) => {
      tmp.set(vivid[i % vivid.length]);
      tmp.getHSL(hsl);
      const sat = Math.max(GAS_SAT_MIN, hsl.s);
      const order = GAS_HUES.slice().sort((a, b) => {
        const da = Math.min(Math.abs(a - hsl.h), 1 - Math.abs(a - hsl.h));
        const db = Math.min(Math.abs(b - hsl.h), 1 - Math.abs(b - hsl.h));
        return da - db;
      });
      // At least 0.25 apart, circularly. At 0.08 two of the three could still
      // land on neighbouring fills and the whole sign came out one colour.
      const sep = (h) => {
        let m = 1;
        for (const u of used) {
          const d = Math.abs(u - h);
          m = Math.min(m, Math.min(d, 1 - d));
        }
        return m;
      };
      let chosen = null;
      for (const h of order) { if (sep(h) >= 0.25) { chosen = h; break; } }
      if (chosen === null) {
        // The six real fills cannot always give three hues 0.25 apart. When
        // they cannot, take the one that is FURTHEST from what is already used
        // rather than falling back to the nearest match, which would hand back
        // a hue next to one already on the sign.
        let best = -1;
        for (const h of GAS_HUES) {
          const d = sep(h);
          if (d > best) { best = d; chosen = h; }
        }
      }
      used.push(chosen);
      target.setHSL(chosen, sat, 0.55);
    };
    assign(0, this._gasTitle);
    assign(1, this._gasArtist);
    assign(2, this._gasFrame);
  }

  // --- the strike ------------------------------------------------------------
  _beginStrike(L) {
    const n = STRIKE_PULSES_MIN + Math.floor(Math.random() * (STRIKE_PULSES_MAX - STRIKE_PULSES_MIN + 1));
    const pulses = [];
    let t = 0;
    for (let i = 0; i < n; i++) {
      t += rand(STRIKE_GAP_MS_MIN, STRIKE_GAP_MS_MAX) / 1000;
      const d = rand(STRIKE_PULSE_MS_MIN, STRIKE_PULSE_MS_MAX) / 1000;
      // Growing: the gas is being persuaded, and each attempt gets further.
      pulses.push([t, t + d, lerp(0.30, 0.95, (i + 1) / n)]);
      t += d;
    }
    L.pulses = pulses;
    L.state = 'striking';
    L.t0 = this._time;
    L.dur = t + rand(0.01, 0.05);
  }

  _setWant(L, on) {
    if (on) {
      if (L.state === 'off') this._beginStrike(L);
    } else if (L.state !== 'off') {
      L.state = 'off'; L.pulses = null;
    }
    L.want = on;
  }

  _stepLetter(L, dt) {
    if (L.state === 'striking') {
      const t = this._time - L.t0;
      let v = 0.03;   // between attempts the tube is not quite dark
      for (const p of L.pulses) if (t >= p[0] && t < p[1]) v = p[2];
      L.lit = v;
      L.front = 1;    // a strike attempt flashes the whole tube at once
      if (t >= L.dur) { L.state = 'propagating'; L.t0 = this._time; L.front = 0; }
    } else if (L.state === 'propagating') {
      const t = (this._time - L.t0) / PROPAGATE_S;
      L.lit = 1;
      L.front = clamp01(t);
      if (t >= 1) { L.state = 'on'; L.front = 1; }
    } else if (L.state === 'on') {
      L.lit = 1; L.front = 1;
    } else {
      L.lit = 0; L.front = 1;
    }
  }

  update(deltaTime, profile) {
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) return;   // freeze, strike included
    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;

    const font = getHersheySans();
    if (!font) return;

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._applyPalette(profile.artworkPalette);
    }

    // Rebuild when the text changes, or when the overlay moves (a longer title
    // wraps to a second line and the safe zone shifts under us).
    const raw = this._sanitise(this._pickText(profile), font);
    // R19: keyed on the stage block, not the cover - a moved cover is no reason to rebuild
    const sc = safeZoneUniforms.uStageCenter.value;
    const sh = safeZoneUniforms.uStageHalfSize.value;
    const safeKey = (sc.y + sh.y).toFixed(3) + ':' + (safeZoneUniforms.uAspect.value || 0).toFixed(2);
    if (raw !== this._textKey || safeKey !== this._lastSafeKey) {
      this._textKey = raw;
      this._lastSafeKey = safeKey;
      this._syncPixel();
      this._textLines = this._wrap(raw);
      const art = this._sanitise(cleanDisplayArtist(profile.artist) || '', font);
      this._artistLine = (art && art !== raw) ? art : '';
      this._build(font, profile);
    }
    if (!this._letters.length) return;

    const director = profile.director;
    const bands = director && director.bands;
    const low = bands && bands.low != null ? clamp01(bands.low) : 0.5;
    const high = bands && bands.high != null ? clamp01(bands.high) : 0.5;
    const progress = profile.progress != null ? clamp01(profile.progress) : 0.5;

    // progress -> how much of the sign is lit in normal running: it fills in
    // over the first half of the track and stays full after that.
    this._litFraction = lerp(PROGRESS_LIT_MIN, 1,
      clamp01(progress / Math.max(PROGRESS_LIT_FULL, 0.01)));

    const body = [];
    for (let i = 0; i < this._letters.length; i++) if (!this._letters[i].frame) body.push(i);

    // --- events ---------------------------------------------------------------
    const beatIndex = profile.beatIndex;
    if (beatIndex != null && beatIndex !== this._lastBeatIndex) {
      if (this._bulbCount) {
        this._chasePos = (this._chasePos + this._chaseDir + this._bulbCount) % this._bulbCount;
      }
      if (this._lastBeatIndex != null && body.length) {
        // A rotating cursor: on each beat the next letter in the pattern comes
        // on, so over a bar the title builds up letter by letter.
        const want = Math.round(this._litFraction * body.length);
        // The faulty letter is exempt from the beat pattern. Otherwise the beat
        // switches it too and its fault stops reading as ITS fault - which is
        // the whole point of there being exactly one.
        let guard = 0;
        do {
          this._beatCursor = (this._beatCursor + 1) % body.length;
          guard++;
        } while (body[this._beatCursor] === this._failIndex && guard <= body.length);
        const idx = body[this._beatCursor];
        const L = this._letters[idx];
        const litCount = body.reduce((n, j) => n + (this._letters[j].want ? 1 : 0), 0);
        this._setWant(L, litCount < want ? true : !L.want);
      }
      const barIndex = profile.barIndex;
      if (barIndex != null && barIndex !== this._lastBarIndex) {
        // The bar: everything on, and a peak - except the faulty letter, which
        // is left to its own cycle.
        for (const j of body) if (j !== this._failIndex) this._setWant(this._letters[j], true);
        this._barPeakUntil = this._time + 0.12;
      }
      this._lastBarIndex = profile.barIndex;
    }
    this._lastBeatIndex = beatIndex;

    // Phrase: the frame runs a chase around its perimeter.
    const phraseIndex = profile.phraseIndex;
    if (phraseIndex != null && this._lastPhraseIndex != null && phraseIndex !== this._lastPhraseIndex) {
      this._chaseUntil = this._time + 1.1;
      this._chaseT0 = this._time;
      this._chaseDir = -this._chaseDir;
    }
    this._lastPhraseIndex = phraseIndex;

    // Drop: the whole sign sizzles - everything drops out at random with failed
    // strikes, then comes back on and holds.
    const mode = director ? director.mode : null;
    if ((mode === 'drop' && this._lastMode !== 'drop') || this._debugGlitch) {
      const spb = (profile.trackFeatures && profile.trackFeatures.bpm)
        ? 60 / profile.trackFeatures.bpm : 0.5;
      this._sizzleUntil = this._time + DROP_SIZZLE_BEATS * spb;
      this._debugGlitch = false;
      if (this._layout.headlights && this._sweepT0 < 0) this._sweepT0 = this._time;
    }
    this._lastMode = mode;
    const sizzling = this._time < this._sizzleUntil;
    if (sizzling && this._time >= this._sizzleNext) {
      this._sizzleNext = this._time + rand(0.02, 0.07);
      for (const j of body) {
        const L = this._letters[j];
        if (Math.random() < 0.55) { L.state = 'off'; L.pulses = null; }
        else if (L.state === 'off') this._beginStrike(L);   // a failed attempt
      }
    } else if (!sizzling && this._sizzleNext > 0) {
      this._sizzleNext = 0;
      for (const j of body) this._setWant(this._letters[j], true);
    }

    // The faulty letter: one, always the same one for this track, going out at
    // uneven intervals and striking again each time.
    if (this._failIndex >= 0 && !sizzling && this._time >= this._failNextAt) {
      const L = this._letters[this._failIndex];
      if (L.state !== 'off') {
        L.state = 'off'; L.pulses = null;
        this._failNextAt = this._time + rand(FAIL_DARK_MS_MIN, FAIL_DARK_MS_MAX) / 1000;
      } else {
        this._beginStrike(L);
        this._failNextAt = this._time + rand(FAIL_MIN_S, FAIL_MAX_S);
      }
    }

    if (this._debugStrike) {
      this._debugStrike = false;
      for (const L of this._letters) { L.state = 'off'; L.pulses = null; }
      for (const L of this._letters) this._setWant(L, true);
    }

    // The frame is always lit, except during a sizzle, and runs its chase.
    // The frame holds lit; the arrow past it runs its own sequence.
    const frameEnd = this._arrowStart >= 0 ? this._arrowStart : this._letters.length;
    for (let i = this._frameLetterStart; i < frameEnd; i++) {
      const L = this._letters[i];
      if (sizzling) { L.state = 'off'; L.lit = 0; }
      else if (L.state === 'off') this._beginStrike(L);
    }

    // --- advance every letter and write the attributes -------------------------
    const litArr = this._litAttr.array;
    const frontArr = this._frontAttr.array;
    const chase = this._time < this._chaseUntil
      ? clamp01((this._time - this._chaseT0) / 1.1) : -1;
    for (let i = 0; i < this._letters.length; i++) {
      const L = this._letters[i];
      this._stepLetter(L, dt);
      let v = L.lit;
      let f = L.front;
      if (L.frame && chase >= 0) {
        // The chase: the front is already an arc position along the frame's
        // perimeter, so sweeping it repeatedly runs the light round the
        // rectangle - a chenillard, out of machinery that already exists.
        f = (chase * 2.0) % 1.0;
        v = 1;
      }
      if (this._forceOff) { v = 0; f = 1; }
      // Fill by range: the value is constant within a letter, so this is ~30
      // fills rather than one write per vertex.
      litArr.fill(v, L.start, L.start + L.count);
      frontArr.fill(f, L.start, L.start + L.count);
    }
    this._litAttr.needsUpdate = true;
    this._frontAttr.needsUpdate = true;

    // --- the garland ----------------------------------------------------------
    // One step per beat, direction flipped on a phrase. The bulbs themselves
    // never switch: they heat and cool, so the running band leaves a warm tail
    // behind it instead of a hard edge.
    if (this._garland && this._bulbCount) {
      const temps = this._bulbTemp.array;
      const heat = Math.min(1, BULB_HEAT * dt);
      const cool = Math.min(1, BULB_COOL * dt);
      for (let i = 0; i < this._bulbCount; i++) {
        // Circular distance: the band wraps round the frame.
        let d = ((i - this._chasePos) % this._bulbCount + this._bulbCount) % this._bulbCount;
        if (d > this._bulbCount / 2) d -= this._bulbCount;
        const on = Math.abs(d) < CHASE_LIT ? 1 : 0;
        const cur = temps[i];
        temps[i] = cur + (on - cur) * (on > cur ? heat : cool);
      }
      this._bulbTemp.needsUpdate = true;
    }

    // --- the arrow ------------------------------------------------------------
    // One cycle every two bars, read straight off the bar phase so it cannot
    // drift away from the music.
    if (this._arrowStart >= 0 && profile.barPhase != null) {
      const barIdx = profile.barIndex != null ? profile.barIndex : 0;
      const phase = ((barIdx % 2) + profile.barPhase) / 2;
      const lead = Math.floor(phase * (ARROW_SEGMENTS + 1.5));
      for (let i = 0; i < ARROW_SEGMENTS; i++) {
        const L = this._letters[this._arrowStart + i];
        if (!L) continue;
        if (i <= lead) { if (L.state === 'off') this._beginStrike(L); }
        else if (L.state !== 'off') { L.state = 'off'; L.pulses = null; }
      }
    }

    // --- rain -----------------------------------------------------------------
    if (this._rain) {
      const Hh = this._halfH;
      const asp = safeZoneUniforms.uAspect.value || 1.6;
      // bands.mid decides how much of it is falling at all.
      const live = Math.round(lerp(0.25, 1.0, this._midSmooth) * this._rainCount);
      const arr = this._rainPos.array;
      for (let i = 0; i < this._rainCount; i++) {
        if (i >= live) { arr[i * 6] = arr[i * 6 + 3] = 1e6; continue; }
        this._rainY[i] -= this._rainV[i] * dt;
        if (this._rainY[i] < -Hh * 1.05) {
          this._rainY[i] = Hh * 1.05;
          this._rainX[i] = (Math.random() * 2 - 1) * asp * Hh;
        }
        const x = this._rainX[i], y = this._rainY[i];
        arr[i * 6] = x; arr[i * 6 + 1] = y; arr[i * 6 + 2] = 0.05;
        arr[i * 6 + 3] = x; arr[i * 6 + 4] = y + this._rainL[i]; arr[i * 6 + 5] = 0.05;
      }
      this._rainPos.needsUpdate = true;
      this._rainUniforms.uSignHalf.value.copy(this._wallUniforms.uSignHalf.value);
      this._rainUniforms.uSignCentre.value.copy(this._wallUniforms.uSignCentre.value);
      this._rainUniforms.uGas.value.copy(this._gasTitle);
    }

    // --- headlights -----------------------------------------------------------
    // Rare and brief on purpose: a car every twenty seconds or so, and a drop
    // brings one early.
    if (this._layout.headlights) {
      if (this._sweepT0 < 0 && this._time >= this._nextHeadlightAt) {
        this._sweepT0 = this._time;
        this._nextHeadlightAt = this._time + rand(HEADLIGHT_MIN_S, HEADLIGHT_MAX_S);
      }
      if (this._sweepT0 >= 0) {
        const u = (this._time - this._sweepT0) / HEADLIGHT_SWEEP_S;
        if (u >= 1) { this._sweepT0 = -1; this._sweep = -2; }
        else this._sweep = -1.35 + u * 2.7;
      }
      this._wallUniforms.uSweep.value = this._sweep;
    }

    // --- the camera -----------------------------------------------------------
    // A slow lateral travelling on top of scene-manager's own drift, and its
    // only job is parallax: the sign, its clips and the wall sit at different
    // depths and a still frame never shows that.
    // The WHOLE position, every frame - not just x. scene-manager re-bases its
    // own drift on whatever the visualizer last wrote, so writing one component
    // leaves the other two carrying last frame's drift, which it then re-bases
    // onto and adds to again: the camera climbed to y = 5.8 in a few seconds
    // and the sign went off screen entirely.
    this._camX = Math.sin(this._time * Math.PI * 2 / CAM_PERIOD_S) * CAM_TRAVEL;
    this._camera.position.set(this._camX, 0, CAMERA_DIST);
    // And the ORIENTATION too. scene-manager re-bases position AND quaternion
    // together the moment either one changes, so writing the position alone
    // makes it re-base onto its own accumulated rotation drift and compound it
    // - the wall was visibly tilting over. Aimed straight ahead rather than at
    // the origin: a travelling shot slides, it does not swivel to keep the
    // subject centred, and sliding is what produces the parallax.
    this._camera.lookAt(this._camX, 0, 0);

    // --- the transformer's hum -------------------------------------------------
    const humHz = lerp(HUM_HZ_MIN, HUM_HZ_MAX, high);
    this._humPhase = (this._humPhase + dt * humHz * Math.PI * 2) % (Math.PI * 2);
    const hum = 1 + HUM_DEPTH * Math.sin(this._humPhase);
    const peak = this._time < this._barPeakUntil ? 1 + BAR_PEAK : 1;
    const haloGain = lerp(0.75, 1.35, low);

    let anyLit = 0;
    for (const L of this._letters) anyLit = Math.max(anyLit, L.lit);
    if (this._forceOff) anyLit = 0;

    for (const m of this._layerMeshes) {
      const u = m.material.uniforms;
      u.uHum.value = hum;
      u.uIntensity.value = peak * (m.userData.mode === 'halo' ? haloGain : 1);
    }

    if (this._fittingUniforms) {
      this._fittingUniforms.uGas.value.copy(this._gasTitle);
      this._fittingUniforms.uIntensity.value = anyLit;
    }
    this._wallUniforms.uGasA.value.copy(this._gasTitle);
    this._wallUniforms.uGasB.value.copy(this._gasArtist);
    this._wallUniforms.uIntensity.value = anyLit * peak * haloGain;

    const aspect = safeZoneUniforms.uAspect.value || 1.6;
    // Le recul le plus défavorable : le mur est déjà à MUR_Z, et la dérive
    // peut encore éloigner la caméra de DERIVE_POS.
    const recul = (CAMERA_DIST - MUR_Z + DERIVE_POS) / CAMERA_DIST;
    const marge = CAM_TRAVEL + DERIVE_POS + DERIVE_ROT * (CAMERA_DIST - MUR_Z) + MARGE_MUR;
    const demiW = this._halfH * recul * aspect + marge;
    const demiH = this._halfH * recul + marge;
    this._wall.scale.set(demiW * 2, demiH * 2, 1);
    this._wall.position.set(0, 0, MUR_Z);
    // uAspect EST la demi-étendue réelle du plan, pas une valeur voisine : le
    // fragment s'en sert pour retrouver sa position en unités monde, et la
    // brique fait 0.62 par 0.24 dans ces unités-là. L'ancienne version posait
    // l'échelle avec un facteur 1.02 et uAspect sans, donc la brique était
    // étirée de 2% - invisible, mais faux, et le moindre agrandissement du
    // mur aurait étiré la brique d'autant.
    this._wallUniforms.uAspect.value.set(demiW, demiH);

    // The floor occupies everything below the waterline.
    if (this._floor.visible) {
      const H = this._halfH;
      // Le sol suit le mur : même largeur, même raison. Il descend aussi plus
      // bas, parce que la dérive fait monter la caméra autant qu'elle la
      // déplace de côté. Le haut reste à la ligne d'eau : le fondu de
      // l'horizon est posé sur vUv.y près de 1, donc sur le HAUT du plan, et
      // l'allonger par le bas ne le déplace pas.
      const halfW = demiW;
      const top = FLOOR_TOP * H, bottom = -demiH;
      this._floor.scale.set(halfW * 2, top - bottom, 1);
      this._floor.position.set(0, (top + bottom) * 0.5, SOL_Z);
      this._floorUniforms.uExtent.value.set(halfW, (top - bottom) * 0.5);
      this._floorUniforms.uTime.value = this._time;
      this._floorUniforms.uIntensity.value = anyLit * peak * haloGain;
      this._floorUniforms.uGasA.value.copy(this._gasTitle);
      this._floorUniforms.uGasB.value.copy(this._gasArtist);
      this._floorUniforms.uSignX.value = this._wallUniforms.uSignCentre.value.x;
    }
    // bands.low drives how hard the water is moving.
    const wob = lerp(0.012, 0.055, low);
    for (const m of this._layerMeshes) {
      if (!m.userData.mirror) continue;
      m.material.uniforms.uTime.value = this._time;
      m.material.uniforms.uWob.value = wob;
    }
  }

  dispose() {
    this._disposeTubes();
    this._disposeRain();
    this._scene.remove(this._group);
    this._floor.geometry.dispose();
    this._floor.material.dispose();
    this._wall.geometry.dispose();
    this._wall.material.dispose();
  }
}
